// Nepal phone numbers (DESIGN_SYSTEM §9.3). Storage and the API use E.164 (`+9779851234567`);
// grouping (`985 123 4567`) is display-only. The mobile pattern `^9[678]\d{8}$` is a proposal to be
// confirmed against the Nepal Telecommunications Authority numbering plan.
import { normalizeDigits } from "./digits";

export const NEPAL_CALLING_CODE = "977";
export const NEPAL_MOBILE_PATTERN = /^9[678]\d{8}$/;

export type PhoneParseError = "empty" | "invalid_characters" | "length" | "prefix";
export type PhoneParseResult =
  { ok: true; e164: string; national: string } | { ok: false; error: PhoneParseError };

/**
 * Normalises pasted or typed input: `+977-9851234567`, `9779851234567`, `00977 985…` and
 * `९८५१२३४५६७` all become `+9779851234567`.
 */
export function parseNepalMobile(input: string): PhoneParseResult {
  const text = normalizeDigits(input).replace(/[\s\-().\u00A0]/g, "");
  if (text === "") return { ok: false, error: "empty" };
  if (!/^\+?\d+$/.test(text)) return { ok: false, error: "invalid_characters" };
  let digits = text.replace(/^\+/, "");
  if (digits.startsWith("00" + NEPAL_CALLING_CODE)) digits = digits.slice(5);
  else if (digits.startsWith(NEPAL_CALLING_CODE) && digits.length === 13) digits = digits.slice(3);
  if (digits.length !== 10) return { ok: false, error: "length" };
  if (!NEPAL_MOBILE_PATTERN.test(digits)) return { ok: false, error: "prefix" };
  return { ok: true, e164: `+${NEPAL_CALLING_CODE}${digits}`, national: digits };
}

/** Groups up to 10 national digits as `985 123 4567` (also for partial input while typing). */
export function groupNationalDigits(digits: string): string {
  const d = digits.slice(0, 10);
  return [d.slice(0, 3), d.slice(3, 6), d.slice(6)].filter(Boolean).join(" ");
}

/** `+9779851234567` → `985 123 4567` (national) or `+977 985 123 4567` (international). */
export function formatPhone(
  e164: string,
  style: "national" | "international" = "national",
): string {
  const m = /^\+977(\d{10})$/.exec(e164);
  if (!m?.[1]) return e164; // Not a Nepal mobile in E.164: show as stored, never guess.
  const grouped = groupNationalDigits(m[1]);
  return style === "international" ? `+977 ${grouped}` : grouped;
}

/** Masked display for roles without `customers.pii.read`: `\u2022\u2022\u2022 \u2022\u2022\u2022 4567`. */
export function maskPhone(last4: string): string {
  return `\u2022\u2022\u2022 \u2022\u2022\u2022 ${last4}`;
}
